'use client';

import { useEffect, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import Logger from '@framework/sentry/Logger';
import { getBrowserTimeZone } from '@/creditflowresearch/application/User/UserTimeZone/helpers/getBrowserTimeZone';
import ContactRepository from '@/creditflowresearch/infrastructure/repository/ContactRepository';
import type { IDictionaryTimeZone } from '@/creditflowresearch/domain/vo/IDictionary';

interface IParams {
  shouldAutoUpdateTimeZone: boolean,
  getByTimeZone: (timeZone?: string | null) => IDictionaryTimeZone | undefined,
  onDetected: (timeZone: string) => void,
}

/**
 * Saves the browser's time zone once, for a user who has none stored yet.
 * Returns true after a successful save so the UI can announce it.
 */
const useDetectUserTimeZone = ({ shouldAutoUpdateTimeZone, getByTimeZone, onDetected }: IParams): boolean => {
  const [isDetected, setIsDetected] = useState(false);
  const container = getContainer();

  useEffect(() => {
    if (!shouldAutoUpdateTimeZone) return;

    const browserTimeZone = getBrowserTimeZone();
    if (!browserTimeZone || !getByTimeZone(browserTimeZone)) return;

    const contactRepository = container.get(ContactRepository);
    contactRepository
      .saveContactSettings({ timezone: browserTimeZone })
      .then(() => {
        onDetected(browserTimeZone);
        setIsDetected(true);
      })
      .catch((error) => {
        Logger.error({ message: 'Failed to auto-detect user time zone', error });
      });
  }, [shouldAutoUpdateTimeZone, getByTimeZone, onDetected, container]);

  return isDetected;
};

export default useDetectUserTimeZone;
